import type { FamilyScopedRow, IsoTimestamp, SyncedRow, Uuid } from "./base.js";
import type { CategoryKey, ChainKey } from "../categories/generated.js";

export type MemberRole = "owner" | "member";

/** Mengeneinheiten der Oberfläche. Umgerechnet wird in `packages/core/units`. */
export const UNITS = ["stk", "g", "kg", "ml", "l", "pkg", "dose", "glas", "flasche", "bund", "becher"] as const;
export type Unit = (typeof UNITS)[number];

/**
 * Die Familie selbst. Sie trägt als einzige Tabelle keine `family_id`, sie
 * ist die Familie. Angelegt wird sie ausschließlich über create_family(),
 * geändert über die Outbox-Operation "update".
 */
export interface Family extends SyncedRow {
  name: string;
  monthly_budget_cents: number | null;
}

/** Einladungscode einer Familie. Zeitlich begrenzt und mit Nutzungszähler. */
export interface Invite extends FamilyScopedRow {
  code: string;
  created_by: Uuid | null;
  expires_at: IsoTimestamp;
  max_uses: number;
  used_count: number;
}

export interface Member extends FamilyScopedRow {
  user_id: Uuid;
  display_name: string;
  role: MemberRole;
}

export interface Store extends FamilyScopedRow {
  name: string;
  chain: ChainKey | null;
  /** Kategorien in Laufrichtung. Startwert aus dem Kettenprofil, lernt je Filiale nach. */
  category_order: CategoryKey[];
  is_default: boolean;
}

export interface List extends FamilyScopedRow {
  name: string;
  store_id: Uuid | null;
  is_default: boolean;
  sort_index: number;
}

export interface Item extends FamilyScopedRow {
  list_id: Uuid;
  product_id: Uuid | null;
  name: string;
  normalized_name: string;
  quantity: number | null;
  unit: Unit | null;
  note: string | null;
  category: CategoryKey | null;
  is_checked: boolean;
  checked_at: IsoTimestamp | null;
  checked_by: Uuid | null;
  is_recurring: boolean;
  /** Wann der Artikel im Laden gefehlt hat. Wird beim Abschluss zurückgesetzt. */
  unavailable_at: IsoTimestamp | null;
  /** Pfad in der Ablage item-photos, sobald das Bild übertragen ist. */
  photo_path: string | null;
  /**
   * Nur kaufen, wenn es im Angebot ist (Migration 0017). Zeilen, die vor dem
   * 17.10.2026 in Dexie gelandet sind, haben das Feld nicht, deshalb immer
   * mit `=== true` lesen.
   */
  only_on_sale: boolean;
  created_by: Uuid | null;
  sort_index: number;
}

export interface Product extends FamilyScopedRow {
  name: string;
  normalized_name: string;
  category: CategoryKey | null;
  default_unit: Unit | null;
  use_count: number;
  is_favorite: boolean;
  last_price_cents: number | null;
}

export interface Trip extends FamilyScopedRow {
  store_id: Uuid | null;
  started_at: IsoTimestamp;
  finished_at: IsoTimestamp | null;
  total_cents: number | null;
  started_by: Uuid | null;
}

/**
 * Ein beobachteter Preis: dieses Produkt hat an diesem Tag in diesem Laden so
 * viel gekostet. Eine Zeile je Bonposition, nie überschrieben. Nur so lässt
 * sich später sagen, wo etwas günstiger war, und nicht bloß, was es zuletzt
 * gekostet hat.
 */
export interface PriceEntry extends FamilyScopedRow {
  product_id: Uuid;
  store_id: Uuid | null;
  trip_id: Uuid | null;
  receipt_id: Uuid | null;
  price_cents: number;
  observed_at: IsoTimestamp;
}

export interface TripItem extends FamilyScopedRow {
  trip_id: Uuid;
  product_id: Uuid | null;
  name: string;
  quantity: number | null;
  unit: Unit | null;
  category: CategoryKey | null;
  price_cents: number | null;
}

/** Ein Rezept. Die Mengen der Zutaten gelten für `servings` Personen. */
/**
 * Woher ein Rezept stammt. Eigene werden hier bearbeitet, gespiegelte gehören
 * der Spaisekammer und sind hier nur zu lesen (Stufe S2).
 */
export const RECIPE_SOURCES = ["eigen", "spaisekammer"] as const;
export type RecipeSource = (typeof RECIPE_SOURCES)[number];

export interface Recipe extends FamilyScopedRow {
  name: string;
  normalized_name: string;
  servings: number;
  note: string | null;
  source_url: string | null;
  created_by: Uuid | null;
  source: RecipeSource;
  /** Kennung drüben. Daran erkennt der Abgleich das Rezept wieder. */
  external_id: string | null;
  /** Link "In der Spaisekammer öffnen". */
  external_url: string | null;
  /** Stand drüben, für den Cursor und die Anzeige. */
  external_updated_at: IsoTimestamp | null;
  /** Anzeigename des Erfassers, für "von Mama" in der Liste. */
  owner_name: string | null;
  /** Signierte Adresse des Titelbilds, sieben Tage gültig. */
  cover_url: string | null;
}

export interface RecipeIngredient extends FamilyScopedRow {
  recipe_id: Uuid;
  product_id: Uuid | null;
  name: string;
  normalized_name: string;
  quantity: number | null;
  unit: Unit | null;
  category: CategoryKey | null;
  /** Salz, Öl, Pfeffer: immer im Haus, wandert nie auf die Liste. */
  is_staple: boolean;
  sort_index: number;
  /** Kennung drüben, damit "Immer im Haus" den Abgleich überlebt. */
  external_id: string | null;
}

/**
 * Verbindung zu einer Partner-App (Stufe S2). Geschrieben wird sie nur aus den
 * Edge Functions mit der Service Role, die App liest sie wie jede andere
 * Tabelle. Das Token selbst liegt im Vault, hier steht nur der Zeiger darauf.
 */
export const PARTNER_STATUS = ["verbunden", "getrennt"] as const;
export type PartnerStatus = (typeof PARTNER_STATUS)[number];

export interface PartnerConnection extends FamilyScopedRow {
  provider: "spaisekammer";
  household_id: string;
  household_name: string;
  linked_by: Uuid | null;
  linked_at: IsoTimestamp;
  token_secret_id: Uuid;
  webhook_secret_hash: string;
  cursor_at: IsoTimestamp | null;
  last_sync_started_at: IsoTimestamp | null;
  last_sync_at: IsoTimestamp | null;
  /** Fehler des letzten Abgleichs als deutscher Satz, sonst null. */
  last_error: string | null;
  recipe_count: number;
  status: PartnerStatus;
}

/** Wo ein Vorrat liegt. Gruppiert die Ansicht, sonst nichts. */
export const PANTRY_LOCATIONS = ["vorrat", "kuehlschrank", "gefrierer"] as const;
export type PantryLocation = (typeof PANTRY_LOCATIONS)[number];

export interface PantryItem extends FamilyScopedRow {
  product_id: Uuid | null;
  name: string;
  normalized_name: string;
  quantity: number;
  /** Ab hier soll nachgekauft werden. Ohne Wert gibt es keine Warnung. */
  min_quantity: number | null;
  unit: Unit | null;
  category: CategoryKey | null;
  location: PantryLocation;
}

/**
 * Ein Eintrag im Wochenplan. Entweder ein Rezept oder ein freier Text
 * ("Reste", "Essen gehen"), nie beides leer.
 */
export interface MealPlanEntry extends FamilyScopedRow {
  /** Ein Tag als YYYY-MM-TT, kein Zeitpunkt. */
  day: string;
  recipe_id: Uuid | null;
  note: string | null;
  /** Abweichende Personenzahl. Ohne Wert gilt die des Rezepts. */
  servings: number | null;
  sort_index: number;
}
